import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bell, BookOpen, Buildings, CalendarBlank, Clock, Crosshair, DeviceMobile, Gauge, Gavel, Gear, Gift, Hourglass, IdentificationBadge, Invoice, List, MapPin, Money, Path, Scroll, SignOut, Users, WarningOctagon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, roles: null, end: true, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients & Contracts", icon: Buildings, roles: ["system_admin", "operations_manager", "finance_officer", "auditor"], testid: "nav-clients" },
  { to: "/sites", label: "Sites & Posts", icon: MapPin, roles: ["system_admin", "operations_manager", "scheduler", "field_supervisor", "auditor"], testid: "nav-sites" },
  { to: "/guards", label: "Guards", icon: IdentificationBadge, roles: ["system_admin", "operations_manager", "hr_admin", "scheduler", "field_supervisor", "auditor"], testid: "nav-guards" },
  { to: "/reserved", label: "Reserved Status", icon: Hourglass, roles: ["system_admin", "operations_manager", "hr_admin", "scheduler", "field_supervisor", "auditor", "payroll_admin"], testid: "nav-reserved" },
  { to: "/firearms", label: "Armoury", icon: Crosshair, roles: ["system_admin", "operations_manager", "hr_admin", "field_supervisor", "scheduler", "auditor"], testid: "nav-firearms" },
  { to: "/schedule", label: "Schedule", icon: CalendarBlank, roles: ["system_admin", "operations_manager", "scheduler", "field_supervisor", "auditor"], testid: "nav-schedule" },
  { to: "/attendance", label: "Attendance", icon: Clock, roles: ["system_admin", "operations_manager", "scheduler", "field_supervisor", "payroll_admin", "auditor"], testid: "nav-attendance" },
  { to: "/tours", label: "Guard Tours", icon: Path, roles: ["system_admin", "operations_manager", "scheduler", "field_supervisor", "hr_admin", "auditor"], testid: "nav-tours" },
  { to: "/incidents", label: "Incidents", icon: WarningOctagon, roles: ["system_admin", "operations_manager", "scheduler", "field_supervisor", "hr_admin", "auditor"], testid: "nav-incidents" },
  { to: "/wage-orders", label: "Wage Orders", icon: Gavel, roles: ["system_admin", "operations_manager", "payroll_admin", "finance_officer", "hr_admin", "auditor"], testid: "nav-wage-orders" },
  { to: "/payroll", label: "Payroll Prep", icon: Money, roles: ["system_admin", "payroll_admin", "auditor"], testid: "nav-payroll" },
  { to: "/benefits", label: "Statutory Benefits", icon: Gift, roles: ["system_admin", "operations_manager", "payroll_admin", "finance_officer", "hr_admin", "auditor"], testid: "nav-benefits" },
  { to: "/invoices", label: "Invoices", icon: Invoice, roles: ["system_admin", "finance_officer", "operations_manager", "auditor"], testid: "nav-billing" },
  { to: "/users", label: "Users & Roles", icon: Users, roles: ["system_admin"], testid: "nav-users" },
  { to: "/audit", label: "Audit Log", icon: Scroll, roles: ["system_admin", "auditor"], testid: "nav-audit" },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["system_admin", "operations_manager"], testid: "nav-settings" },
  // Everyone, including the auditor and anyone who will never open Settings:
  // a person is entitled to read how the system that pays them works.
  { to: "/docs", label: "How this works", icon: BookOpen, roles: null, testid: "nav-docs" },
];

const ROLE_LABELS = {
  system_admin: "System Admin", operations_manager: "Operations Manager", scheduler: "Scheduler",
  hr_admin: "HR Admin", payroll_admin: "Payroll Admin", finance_officer: "Finance / Billing",
  field_supervisor: "Field Supervisor", guard: "Security Guard", auditor: "Auditor",
};

export default function ConsoleLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState(null);

  useEffect(() => {
    const load = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const toggleNotifs = async () => {
    if (notifs) { setNotifs(null); return; }
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
      await api.post("/notifications/read-all");
      setUnread(0);
    } catch (e) { /* ignore */ }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className={`fixed inset-y-0 left-0 z-40 border-r border-border bg-card flex flex-col transition-[width] duration-150 ${collapsed ? "w-14" : "w-60"}`} data-testid="console-sidebar">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <div className="w-6 h-6 bg-foreground text-background grid place-items-center font-heading font-black text-xs shrink-0">A</div>
          {!collapsed && <span className="font-heading font-black tracking-tight">AEGIS<span className="text-[hsl(var(--warning))]">OPS</span></span>}
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.filter((n) => !n.roles || n.roles.includes(user.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 mx-1 text-sm transition-colors duration-150 ${isActive ? "bg-foreground text-background" : "hover:bg-secondary"}`
              }
            >
              <n.icon size={18} weight="duotone" className="shrink-0" />
              {!collapsed && <span className="truncate">{n.label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-2">
          {["field_supervisor", "operations_manager", "scheduler", "system_admin"].includes(user.role) && (
            <button data-testid="field-app-link" onClick={() => navigate("/m")}
              className="w-full flex items-center gap-3 px-2 py-2 text-sm hover:bg-secondary transition-colors duration-150">
              <DeviceMobile size={18} weight="duotone" className="shrink-0" />
              {!collapsed && "Field App"}
            </button>
          )}
          <button data-testid="logout-button" onClick={doLogout}
            className="w-full flex items-center gap-3 px-2 py-2 text-sm hover:bg-secondary transition-colors duration-150">
            <SignOut size={18} weight="duotone" className="shrink-0" />
            {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      <div className={`${collapsed ? "ml-14" : "ml-60"} transition-[margin] duration-150`}>
        <header className="sticky top-0 z-30 h-12 border-b border-border bg-background flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)} className="p-1.5 hover:bg-secondary transition-colors duration-150">
              <List size={18} />
            </button>
            <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Office Console</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button data-testid="notifications-bell" onClick={toggleNotifs} className="p-1.5 hover:bg-secondary transition-colors duration-150 relative">
                <Bell size={18} weight={unread > 0 ? "fill" : "duotone"} />
                {unread > 0 && (
                  <span data-testid="notifications-unread-badge" className="absolute -top-0.5 -right-0.5 bg-[hsl(var(--alert))] text-white font-mono text-[9px] px-1 min-w-[14px] text-center">
                    {unread}
                  </span>
                )}
              </button>
              {notifs && (
                <div className="absolute right-0 top-9 w-80 border border-border bg-card z-50 max-h-96 overflow-auto" data-testid="notifications-panel">
                  <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-[0.1em] font-bold">Notifications</div>
                  {notifs.length === 0 && <div className="px-3 py-6 text-xs text-muted-foreground font-mono">No notifications</div>}
                  {notifs.map((n) => (
                    <div key={n.id} className={`px-3 py-2 border-b border-border ${n.read ? "opacity-60" : ""}`}>
                      <div className="text-sm font-semibold">{n.title}</div>
                      <div className="text-xs text-muted-foreground">{n.body}</div>
                      <div className="font-mono text-[10px] text-muted-foreground mt-1">{fmtDT(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold leading-tight" data-testid="current-user-name">{user.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground uppercase">{ROLE_LABELS[user.role] || user.role}</div>
            </div>
          </div>
        </header>
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
